import { useEffect, useRef, useState } from "react";

export interface McpAppsIframeHostProps {
  widgetUri: string;
  serverId: string;
  toolCallId: string;
  toolName?: string | null;
  toolInput: any;
  toolOutput: any;
  onSendFollowUp?: (message: string) => void;
  onCallTool?: (toolName: string, params: any) => Promise<any>;
}

interface JsonRpcMessage {
  jsonrpc: "2.0";
  method: string;
  params?: unknown;
  id?: string | number;
}

/**
 * MCP Apps iframe host component for chat
 *
 * Reuses the same sandbox pattern as WidgetPreview:
 * - Outer iframe: /mcp-ui-sandbox.html (with import map)
 * - Sends widget HTML via ui/sandbox-resource-ready
 * - Communicates via JSON-RPC protocol
 */
export function McpAppsIframeHost(props: McpAppsIframeHostProps) {
  const {
    widgetUri,
    serverId,
    toolCallId,
    toolName,
    toolInput,
    toolOutput,
    onSendFollowUp,
    onCallTool,
  } = props;

  const sandboxRef = useRef<HTMLIFrameElement>(null);
  const hasSentToolResult = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [widgetHtml, setWidgetHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Dynamic height for widget iframe
  const MIN_WIDGET_HEIGHT = 520;
  const MAX_WIDGET_HEIGHT = 900;
  const [iframeHeight, setIframeHeight] = useState<number>(MIN_WIDGET_HEIGHT);

  // Step 1: Store widget data (reuse existing endpoint)
  useEffect(() => {
    async function storeWidget() {
      try {
        const response = await fetch("/api/mcp/openai/widget/store", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            serverId,
            uri: widgetUri, // e.g., "ui://main"
            toolInput,
            toolOutput,
            toolId: toolCallId,
            toolName: toolName || "",
          }),
        });

        if (!response.ok) {
          throw new Error(
            `Failed to store widget data: ${response.statusText}`,
          );
        }
      } catch (err) {
        console.error("[MCP Apps Chat] Error storing widget:", err);
        setError("Failed to store widget data");
        setLoading(false);
      }
    }

    storeWidget();
    // Only re-run if the tool call ID changes, not if input/output objects get new references
  }, [serverId, widgetUri, toolCallId, toolName]);

  // Step 2: Fetch widget HTML (reuse existing endpoint)
  useEffect(() => {
    async function fetchWidget() {
      try {
        const response = await fetch(
          `/api/mcp/openai/widget-content/${toolCallId}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch widget: ${response.statusText}`);
        }

        const html = await response.text();
        setWidgetHtml(html);
        setLoading(false);
      } catch (err) {
        console.error("[MCP Apps Chat] Error fetching widget HTML:", err);
        setError("Failed to fetch widget HTML");
        setLoading(false);
      }
    }

    fetchWidget();
  }, [toolCallId]);

  // Step 3: Listen for sandbox-ready and handle JSON-RPC messages
  useEffect(() => {
    async function handleCallServerTool(
      id: string | number | undefined,
      params: any,
    ) {
      try {
        const calledToolName = params?.name;
        const calledToolArgs = params?.arguments ?? {};

        if (!calledToolName) {
          if (id != null && sandboxRef.current?.contentWindow) {
            sandboxRef.current.contentWindow.postMessage(
              {
                jsonrpc: "2.0",
                id,
                error: { message: "Missing tool name" },
              },
              "*",
            );
          }
          return;
        }

        // Call the tool via onCallTool prop
        if (onCallTool) {
          const result = await onCallTool(calledToolName, calledToolArgs);

          // Send JSON-RPC response
          if (id != null && sandboxRef.current?.contentWindow) {
            sandboxRef.current.contentWindow.postMessage(
              {
                jsonrpc: "2.0",
                id,
                result,
              },
              "*",
            );
          }

          // Also send notification so app.ontoolresult() is called
          sandboxRef.current?.contentWindow?.postMessage(
            {
              jsonrpc: "2.0",
              method: "mcp.ext-apps/notifyToolResult",
              params: result,
            },
            "*",
          );
        }
      } catch (error: any) {
        console.error("[MCP Apps Chat] handleCallServerTool error", error);
        if (id != null && sandboxRef.current?.contentWindow) {
          sandboxRef.current.contentWindow.postMessage(
            {
              jsonrpc: "2.0",
              id,
              error: { message: error?.message || "Failed to call tool" },
            },
            "*",
          );
        }
      }
    }

    function handleMessage(event: MessageEvent) {
      const data = event.data;

      // Ignore invalid messages
      if (!data || typeof data !== "object") {
        return;
      }

      // Handle widget resize messages
      if (data.method === "mcp.ext-apps/widget-resized") {
        const height = data.params?.height;
        if (typeof height === "number" && Number.isFinite(height)) {
          const clamped = Math.max(
            MIN_WIDGET_HEIGHT,
            Math.min(MAX_WIDGET_HEIGHT, height),
          );
          setIframeHeight(clamped);
        }
        return;
      }

      // Handle sandbox-ready message
      if (data.method === "ui/sandbox-ready") {
        console.log("[MCP Apps Chat] Sandbox ready");
        setIsReady(true);
        return;
      }

      // Handle MCP Apps JSON-RPC messages
      if (data.jsonrpc === "2.0" && typeof data.method === "string") {
        const { method, id, params } = data;

        switch (method) {
          case "mcp.ext-apps/appReady":
            console.log("[MCP Apps Chat] Widget app is ready");

            // Now send the initial tool result
            if (
              sandboxRef.current?.contentWindow &&
              !hasSentToolResult.current
            ) {
              const notifyMsg = {
                jsonrpc: "2.0",
                method: "mcp.ext-apps/notifyToolResult",
                params: toolOutput,
              };
              console.log(
                "[MCP Apps Chat] Sending initial notifyToolResult:",
                notifyMsg,
              );
              console.log("[MCP Apps Chat] toolOutput structure:", {
                hasContent: !!toolOutput?.content,
                hasStructuredContent: !!toolOutput?.structuredContent,
                widgetId: toolOutput?.structuredContent?._widget,
              });
              sandboxRef.current.contentWindow.postMessage(notifyMsg, "*");
              hasSentToolResult.current = true;
            }
            return;

          case "mcp.ext-apps/callServerTool":
            void handleCallServerTool(id, params);
            return;

          case "mcp.ext-apps/sendMessage":
            if (onSendFollowUp && params) {
              const message =
                typeof params === "string"
                  ? params
                  : params.message || params.prompt || "";
              if (message) {
                onSendFollowUp(message);
              }
            }
            return;

          case "mcp.ext-apps/openLink":
            {
              const url =
                params && typeof params === "object" && "url" in params
                  ? params.url
                  : params;
              if (typeof url === "string") {
                window.open(url, "_blank", "noopener,noreferrer");
              }
            }
            return;

          default:
            // Log other MCP Apps message types for debugging
            if (method.startsWith("mcp.ext-apps/")) {
              console.log("[MCP Apps Chat] Unhandled MCP Apps message:", data);
            }
            break;
        }
      }

      // Handle OpenAI-style messages (for compatibility)
      if (data.type) {
        switch (data.type) {
          case "openai:callTool":
            if (onCallTool && sandboxRef.current?.contentWindow) {
              onCallTool(data.toolName, data.params)
                .then((result) => {
                  sandboxRef.current?.contentWindow?.postMessage(
                    {
                      type: "openai:callTool:response",
                      requestId: data.requestId,
                      result,
                    },
                    "*",
                  );
                })
                .catch((error) => {
                  sandboxRef.current?.contentWindow?.postMessage(
                    {
                      type: "openai:callTool:response",
                      requestId: data.requestId,
                      error: error.message,
                    },
                    "*",
                  );
                });
            }
            return;

          case "openai:sendFollowup":
            if (onSendFollowUp) {
              onSendFollowUp(data.message);
            }
            return;

          case "openai:openExternal":
            if (data.href) {
              window.open(data.href, "_blank", "noopener,noreferrer");
            }
            return;
        }
      }
    }

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onCallTool, onSendFollowUp]);

  // Step 4: Send widget HTML to sandbox when ready (only once)
  useEffect(() => {
    if (
      isReady &&
      widgetHtml &&
      sandboxRef.current?.contentWindow &&
      !hasSentToolResult.current
    ) {
      console.log("[MCP Apps Chat] Sending widget HTML to sandbox");

      // Send HTML to sandbox
      const message: JsonRpcMessage = {
        jsonrpc: "2.0",
        method: "ui/sandbox-resource-ready",
        params: { html: widgetHtml },
      };

      sandboxRef.current.contentWindow.postMessage(message, "*");

      // Note: We don't send notifyToolResult here anymore.
      // Instead, we wait for the widget app to send "mcp.ext-apps/appReady"
      // signal after it mounts and registers callbacks. This ensures the
      // ontoolresult callback is set up before we send the tool result.
    }
  }, [isReady, widgetHtml]);

  if (loading) {
    return (
      <div className="p-4 border rounded-md bg-muted/30">
        <p className="text-sm text-muted-foreground">Loading widget...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-destructive/40 rounded-md bg-destructive/10">
        <p className="text-sm text-destructive">Error: {error}</p>
      </div>
    );
  }

  if (!widgetHtml) {
    return (
      <div className="p-4 border rounded-md bg-muted/30">
        <p className="text-sm text-muted-foreground">
          No widget content available
        </p>
      </div>
    );
  }

  return (
    <iframe
      ref={sandboxRef}
      src="/mcp-ui-sandbox.html"
      sandbox="allow-scripts allow-same-origin allow-popups"
      style={{
        width: "100%",
        height: `${iframeHeight}px`,
        border: "none",
        borderRadius: "0.375rem",
        display: "block",
      }}
      title="MCP Apps Widget"
    />
  );
}
